"use client";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useGetAiworkersPresignedUrlMutation, useUploadFileToS3Mutation } from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { UploadedFileType } from '../utils/constants';
import { ATTACHMENT_FILE_INPUT_ACCEPT, uploadAttachmentFiles } from './attachmentUploadShared';

export interface CommentAttachmentSource {
  source_id: string;
  file_name: string;
}

/** Same clipboard extraction as the advisor prompt box: files list first, then file items. */
function filesFromClipboardData(data: DataTransfer | null): File[] {
  if (!data) return [];
  if (data.files?.length) return Array.from(data.files);
  const out: File[] = [];
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file) out.push(file);
    }
  }
  return out;
}

/**
 * Uploads comment/triage attachments using the same presigned-URL + S3 flow as the prompt box.
 * No size limit is enforced here (unlike the live prompt box).
 */
export function useCommentAttachmentUpload() {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileType[]>([]);
  const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();
  const [uploadToS3] = useUploadFileToS3Mutation();

  const addFiles = useCallback(
    (accepted: File[]) =>
      uploadAttachmentFiles(accepted, { getPresignedUrl: getAiworkersPresignedUrl, uploadToS3, setUploadedFiles }),
    [getAiworkersPresignedUrl, uploadToS3]
  );

  const reset = useCallback(() => setUploadedFiles([]), []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const files = filesFromClipboardData(e.clipboardData);
    if (!files.length) return;
    e.preventDefault();
    void addFiles(files);
  }, [addFiles]);

  const sources: CommentAttachmentSource[] = useMemo(
    () =>
      uploadedFiles
        .filter((f) => !!f.source_id)
        .map((f) => ({ source_id: f.source_id as string, file_name: f.name })),
    [uploadedFiles]
  );
  const isUploading = uploadedFiles.some((f) => f.uploading);

  return { uploadedFiles, setUploadedFiles, addFiles, handlePaste, reset, sources, isUploading };
}

/**
 * Attach button + hidden file input, meant to be passed as a Textarea/Input `endContent`
 * (extreme right, inside the input box) — same spot as the prompt box's attach icon.
 * Pair with `AttachmentPreviewSection` (rendered above the input) for the preview chips.
 */
export function CommentAttachmentButton(props: Readonly<{
  addFiles: (files: File[]) => void | Promise<void>;
  disabled?: boolean;
}>) {
  const { addFiles, disabled = false } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ATTACHMENT_FILE_INPUT_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) void addFiles(files);
        }}
      />
      <CtyButton
        id="comment-attachment-upload"
        isIconOnly
        tooltipContent="Attach"
        radius="full"
        size="sm"
        variant="light"
        isDisabled={disabled}
        onPress={() => fileInputRef.current?.click()}
      >
        <Icon className="text-primary cursor-pointer" icon="clarity:attachment-line" width={20} height={20} />
      </CtyButton>
    </>
  );
}
